import { state, getCfg } from './state.js';
import { easeInOutCubic } from './camera-controls.js';
import { computeFramedPositions } from './asset-loader.js';

/* ═══════════════════════════════════════════
   § 9. CORE RENDER LOOP & WINDOW RESIZE
   ═══════════════════════════════════════════ */

// ── Smart Render: 60-second idle sleep ────
// wakeRenderer is exported so audio-hdri.js
// can call it from the Page Visibility handler.
let _animate; // forward-ref for wakeRenderer → animate()

export function wakeRenderer() {
  state._idleTimer = 0;
  if (state._isIdle) {
    state._isIdle = false;
    console.log('[Smart Render] Waking up.');
    _animate(); // Restart loop
  }
}

// ── Main loop ──────────────────────────────
export function initRenderLoop(ctx) {
  const { scene, camera, renderer, controls, clock } = ctx;
  const $ = id => document.getElementById(id);

  function animate() {
    if (state._isIdle) return; // Stop loop when idle
    requestAnimationFrame(animate);
    const delta = Math.min(clock.getDelta(), 0.1);

    // Idle timer
    state._idleTimer += delta;
    if (state._idleTimer >= state.IDLE_TIMEOUT && !state.animTime && !state.currentPin) {
      state._isIdle = true;
      console.log('[Smart Render] Idle for 60s — pausing render loop.');
      return;
    }

    // Sky always rotates (cheap CPU-side), only GPU render is gated
    if (state.skyDome && state.skyLoaded) {
      state.skyDome.rotation.y += 0.0002;
      if (state.skyDome.material.opacity < 1) {
        state.skyDome.material.opacity += delta * 0.5;
        if (state.skyDome.material.opacity > 1) state.skyDome.material.opacity = 1;
      }
    }

    // Camera animation (flyTo / home / 3d toggle)
    if (state.animTime > 0) {
      state.animTime += delta;
      let t = state.animTime / state.animDurationScale;
      if (t >= 1) {
        t = 1;
        state.animTime = 0;
        state.isAnimatingCamera = false;

        camera.position.copy(state.animCameraTarget);
        camera.quaternion.copy(state.endCamQuat);
        controls.target.copy(state.animTarget);

        controls.enabled = true;
        controls.enableDamping = true;
        controls.saveState();
        controls.reset();
        controls.update(); // Sync orbit controls internal state
      } else {
        const ease = easeInOutCubic(t);
        camera.position.lerpVectors(state.startCamPos, state.animCameraTarget, ease);
        camera.quaternion.slerpQuaternions(state.startCamQuat, state.endCamQuat, ease);
        controls.target.lerpVectors(state.startCtrlTarget, state.animTarget, ease);
      }
    }

    // Floating label position update (only when a plot is selected)
    if (state.selectedPlotCenter && state.selectedPlotId) {
      const tempV = state.selectedPlotCenter.clone();
      tempV.project(camera);
      const x = (tempV.x * .5 + .5) * window.innerWidth;
      const y = (tempV.y * -.5 + .5) * window.innerHeight;
      $('fl').style.left = `${x}px`;
      $('fl').style.top  = `${y}px`;
    }

    // Dynamic Floating Pin & Ripple Animation
    if (state.currentPin) {
      const t      = clock.getElapsedTime();
      const isMob  = window.innerWidth < 768;
      const pScale = isMob ? 10 : 16;
      // Slow, luxurious bounce
      state.currentPin.position.y = state.currentPin.userData.baseY + Math.sin(t * 1.5) * 3;
      state.currentPin.scale.set(pScale, pScale, 1);

      const glowPulse = 0.85 + Math.sin(t * 3) * 0.15; // Gentle subtle pulse highlighting
      state.currentPin.material.opacity = Math.min(state.currentPin.material.opacity + delta * 1.5, glowPulse);

      if (state.currentRipple) {
        const waveCycle = (t * 1.2) % 2; // Cycle from 0 to 2
        const rScale = 1 + waveCycle * 1.5; // Expand outwards gently
        state.currentRipple.scale.set(rScale, rScale, 1);
        state.currentRipple.material.opacity = Math.max(0, 0.85 - (waveCycle * 0.42)); // Brighter starting opacity fading out
      }
    }

    if (!state.isAnimatingCamera) controls.update();
    renderer.render(scene, camera);

    // DRS: Warm-Up & Lock — only evaluate after grace period
    if (!state._drsLocked) {
      state._drsFrames++;
      state._drsElapsed += delta;
      if (state._drsElapsed >= 1) { // sample every second
        state._drsSamples.push(state._drsFrames);
        state._drsFrames  = 0;
        state._drsElapsed = 0;
      }
      // After grace period, evaluate once
      if (state._drsSamples.length >= state._DRS_GRACE) {
        const avg = state._drsSamples.reduce((a, b) => a + b, 0) / state._drsSamples.length;
        const currentPR = renderer.getPixelRatio();

        if (avg >= 45) {
          console.log('[DRS] Locked at current quality (' + currentPR.toFixed(2) + '×, ø' + avg.toFixed(0) + ' fps)');
        } else if (avg <= 25) {
          // Use emergency floor if AQC already dropped quality during loading
          const floor = state._aqcActive ? state._AQC_EMERGENCY_FLOOR : state._DRS_FLOOR;
          const newPR = Math.max(currentPR * 0.75, floor);
          renderer.setPixelRatio(newPR);
          console.log('[DRS] Dropped to ' + newPR.toFixed(2) + '× (' + ((newPR / state._nativeDPR) * 100).toFixed(0) + '% native, ø' + avg.toFixed(0) + ' fps)');
        } else {
          console.log('[DRS] Keeping current quality (' + currentPR.toFixed(2) + '×, ø' + avg.toFixed(0) + ' fps)');
        }
        state._drsLocked = true; // never re-evaluate
        // Free dead sampling state
        state._drsSamples = null;
      }
    }
  }

  // Store ref so wakeRenderer() can restart the loop
  _animate = animate;

  // ── Idle wake listeners ──────────────────
  ['pointermove', 'pointerdown', 'wheel', 'keydown'].forEach(evt => {
    document.addEventListener(evt, wakeRenderer, { passive: true });
  });
  // touchstart on canvas only (not document) — avoids intercepting UI scrolling
  renderer.domElement.addEventListener('touchstart', wakeRenderer, { passive: true });

  // ── iOS rubber-band prevention on canvas ────
  // Only prevents default on the 3D canvas element; UI panels (.loc-panel etc.)
  // use their own scrollable containers and are unaffected.
  renderer.domElement.addEventListener('touchmove', (e) => {
    if (e.target === renderer.domElement || renderer.domElement.contains(e.target)) {
      e.preventDefault();
    }
  }, { passive: false });

  // ── Window resize ────────────────────────
  window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
    computeFramedPositions(camera); // Recalculate framed positions for new aspect ratio
    wakeRenderer();
  });

  // ── Compass ──────────────────────────────
  if (getCfg('enableCompass') !== false) {
    $('compass').classList.remove('hide');
  }
  controls.addEventListener('change', () => {
    if (getCfg('enableCompass') !== false) {
      const az               = controls.getAzimuthalAngle();
      const northRadians     = getCfg('northAngle') * Math.PI / 180;
      const cssRotationRadians = az + northRadians;
      $('compass').style.transform = `rotate(${cssRotationRadians}rad)`;
    }
  });

  // Start the loop
  animate();
}

// ── Deep-link: ?plot= query param ─────────
// Called by main.js after the first fetchData()
// so crmData is already populated.
export function checkDeepLink(ctx, { selectPlot, flyTo }) {
  const urlPlot = new URLSearchParams(window.location.search).get('plot');
  if (!urlPlot) return;
  const target = state.crmData.find(d => d.Plot_ID === urlPlot || d.Plot_ID === 'Plot-' + urlPlot);
  if (target) {
    selectPlot(target.Plot_ID);
    if (target.MatName) flyTo(target.MatName, ctx);
  }
}
